import { BP_PANEL_CODE } from './vitals';

/**
 * LOINC codes named in code — the ones a protocol, the day view or a service branches on. Displays
 * live in the curated maps (`LOINC_LAB_DISPLAY_NAMES`, `VITAL_SIGN_CONFIGS`,
 * `LIFESTYLE_MEASUREMENT_CONFIGS`); this is only the identity, so every consumer names a code the same
 * way instead of repeating the literal.
 */
export const LOINC_CODES = {
  /** Hemoglobin A1c/Hemoglobin.total in Blood */
  HBA1C: '4548-4',
  /** Cholesterol in LDL [Mass/volume] in Serum or Plasma */
  LDL_CHOLESTEROL: '13457-7',
  /** Glucose [Mass/volume] in Blood — fingerstick / home meter */
  BLOOD_GLUCOSE: '2339-0',
  /** Blood pressure panel with all children optional */
  BLOOD_PRESSURE_PANEL: BP_PANEL_CODE,
  /** Body weight */
  BODY_WEIGHT: '29463-7',
  /** Number of steps in 24 hour Measured */
  STEPS: '41950-7',
  /** Patient Health Questionnaire 9 item (PHQ-9) total score */
  PHQ9_TOTAL_SCORE: '44261-6',
  /** Cholesterol in HDL [Mass/volume] in Serum or Plasma */
  HDL_CHOLESTEROL: '2085-9',
  /** Triglyceride [Mass/volume] in Serum or Plasma */
  TRIGLYCERIDES: '2571-8',
  /** Cholesterol [Mass/volume] in Serum or Plasma */
  TOTAL_CHOLESTEROL: '2093-3',
  /** Glomerular filtration rate/1.73 sq M.predicted */
  EGFR: '33914-3',
  /** Creatinine [Mass/volume] in Serum or Plasma */
  CREATININE: '2160-0',
  /** Albumin/Creatinine [Mass Ratio] in Urine */
  URINE_ALBUMIN_CREATININE_RATIO: '9318-7',
  /** Fasting glucose [Mass/volume] in Serum or Plasma */
  FASTING_GLUCOSE: '1558-6',
  /** Body mass index (BMI) [Ratio] — already a curated vital in `VITAL_SIGN_CONFIGS` */
  BMI: '39156-5',
  /** Waist Circumference at umbilicus by Tape measure — already a curated vital */
  WAIST_CIRCUMFERENCE: '8280-0',
  /**
   * Oxygen saturation in Arterial blood by Pulse oximetry — already a curated vital. This is the
   * non-invasive bedside reading, distinct from `2708-6`, the arterial blood gas measurement (SaO2).
   */
  OXYGEN_SATURATION: '59408-5',
  /** FEV1 — forced expiratory volume in the first second, from spirometry */
  FEV1: '20150-9',
  /** Maximum expiratory gas flow Respiratory system airway — peak expiratory flow (PEF) */
  PEAK_EXPIRATORY_FLOW: '33452-4',
  /** Natriuretic peptide.B prohormone N-Terminal [Mass/volume] in Serum or Plasma (NT-proBNP) */
  NT_PRO_BNP: '33762-6',
  /** Creatine kinase [Enzymatic activity/volume] in Serum or Plasma */
  CREATINE_KINASE: '2157-6',
  /** Thyrotropin [Units/volume] in Serum or Plasma (TSH) */
  TSH: '3016-3',
  /** Thyroxine (T4) free [Mass/volume] in Serum or Plasma */
  FREE_T4: '3026-2',
  /**
   * Triiodothyronine (T3) [Mass/volume] in Serum or Plasma — *total* T3, the analyte ATA names for
   * hyperthyroidism monitoring, because free T4 can normalise while total T3 stays elevated.
   */
  TOTAL_T3: '3053-6',
  /** Aspartate aminotransferase [Enzymatic activity/volume] in Serum or Plasma */
  AST: '1920-8',
  /** Alanine aminotransferase [Enzymatic activity/volume] in Serum or Plasma */
  ALT: '1742-6',
  /** Platelets [#/volume] in Blood by Automated count */
  PLATELETS: '777-3',
  /** Potassium [Moles/volume] in Serum or Plasma */
  POTASSIUM: '2823-3',
} as const;

export type LoincCode = (typeof LOINC_CODES)[keyof typeof LOINC_CODES];
